import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  Package,
  Truck,
  FileText,
  Menu,
  X,
  Globe,
  LogOut,
  User,
  UserCog,
  ChevronDown,
  Home,
  BarChart3,
  UserCircle,
  ShoppingCart,
  Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUser } from '@/contexts/UserContext';
import { QRButton } from '@/components/qr';

// Customer-only navigation (simple view)
const customerNavigation = [
  { nameKey: 'dashboard', href: '/dashboard', icon: LayoutDashboard, hasSubsections: false },
  { nameKey: 'wallet', href: '/wallet', icon: Wallet, hasSubsections: false },
  {
    nameKey: 'sourcingRequests',
    href: '/sourcing-requests',
    icon: ShoppingCart,
    hasSubsections: true,
    subsections: [
      { key: 'all', nameKey: 'allRequests', label: 'All Requests' },
      { key: 'pending', nameKey: 'pending', label: 'Pending' },
      { key: 'quoted', nameKey: 'quoted', label: 'Quoted' },
      { key: 'approved', nameKey: 'approved', label: 'Approved' },
      { key: 'completed', nameKey: 'completed', label: 'Completed' }
    ]
  },
  {
    nameKey: 'shipments',
    href: '/shipments',
    icon: Package,
    hasSubsections: true,
    subsections: [
      { key: 'all', nameKey: 'allShipments', label: 'All Shipments' },
      { key: 'on-the-way', nameKey: 'onTheWay', label: 'On the Way' },
      { key: 'processing', nameKey: 'processing', label: 'Processing' },
      { key: 'in-transit', nameKey: 'inTransit', label: 'In Transit' },
      { key: 'at-destination', nameKey: 'atDestination', label: 'At Destination' },
      { key: 'delivered', nameKey: 'delivered', label: 'Delivered' }
    ]
  },
  {
    nameKey: 'invoices',
    href: '/invoices',
    icon: FileText,
    adminOnly: false,
    hasSubsections: true,
    subsections: [
      { key: 'all', nameKey: 'allInvoices', label: 'All Invoices' },
      { key: 'sent', nameKey: 'sent', label: 'Sent' },
      { key: 'paid', nameKey: 'paid', label: 'Paid' },
      { key: 'overdue', nameKey: 'overdue', label: 'Overdue' },
      { key: 'partial', nameKey: 'partial', label: 'Partial' }
    ]
  },
  {
    nameKey: 'users',
    href: '/users',
    icon: Users,
    adminOnly: true,
    hasSubsections: false
  },
  {
    nameKey: 'profile',
    href: '/profile',
    icon: User,
    adminOnly: false,
    hasSubsections: false
  }
];

// Admin/Warehouse navigation (full system access)
const navigation = [
  { nameKey: 'dashboard', href: '/dashboard', icon: LayoutDashboard, adminOnly: false, hasSubsections: false },
  { nameKey: 'customers', href: '/customers', icon: Users, adminOnly: false, hasSubsections: false },
  { nameKey: 'walletManagement', href: '/wallet-admin', icon: Wallet, adminOnly: true, hasSubsections: false },
  { nameKey: 'warehouses', href: '/warehouses', icon: Building2, adminOnly: true, hasSubsections: false },
  {
    nameKey: 'sourcingRequests',
    href: '/sourcing-requests',
    icon: ShoppingCart,
    adminOnly: false,
    hasSubsections: true,
    subsections: [
      { key: 'all', nameKey: 'allRequests', label: 'All Requests' },
      { key: 'pending', nameKey: 'pending', label: 'Pending' },
      { key: 'quoted', nameKey: 'quoted', label: 'Quoted' },
      { key: 'approved', nameKey: 'approved', label: 'Approved' },
      { key: 'completed', nameKey: 'completed', label: 'Completed' }
    ]
  },
  {
    nameKey: 'shipments',
    href: '/shipments',
    icon: Package,
    adminOnly: false,
    hasSubsections: true,
    subsections: [
      { key: 'all', nameKey: 'allShipments', label: 'All Shipments' },
      { key: 'on-the-way', nameKey: 'onTheWay', label: 'On the Way' },
      { key: 'processing', nameKey: 'processing', label: 'Processing' },
      { key: 'in-transit', nameKey: 'inTransit', label: 'In Transit' },
      { key: 'at-destination', nameKey: 'atDestination', label: 'At Destination' },
      { key: 'delivered', nameKey: 'delivered', label: 'Delivered' }
    ]
  },
  {
    nameKey: 'trips',
    href: '/trips',
    icon: Truck,
    adminOnly: false,
    hasSubsections: true,
    subsections: [
      { key: 'all', nameKey: 'allTrips', label: 'All Trips' },
      { key: 'planning', nameKey: 'planning', label: 'Planning' },
      { key: 'in-transit', nameKey: 'inTransit', label: 'In Transit' },
      { key: 'at-destination', nameKey: 'atDestination', label: 'At Destination' },
      { key: 'completed', nameKey: 'completed', label: 'Completed' }
    ]
  },
  {
    nameKey: 'invoices',
    href: '/invoices',
    icon: FileText,
    adminOnly: false,
    hasSubsections: true,
    subsections: [
      { key: 'all', nameKey: 'allInvoices', label: 'All Invoices' },
      { key: 'draft', nameKey: 'draft', label: 'Draft' },
      { key: 'sent', nameKey: 'sent', label: 'Sent' },
      { key: 'paid', nameKey: 'paid', label: 'Paid' },
      { key: 'overdue', nameKey: 'overdue', label: 'Overdue' },
      { key: 'partial', nameKey: 'partial', label: 'Partial' }
    ]
  },
  { nameKey: 'reports', href: '/reports', icon: BarChart3, adminOnly: false, hasSubsections: false },
  { nameKey: 'users', href: '/users', icon: UserCog, adminOnly: true, hasSubsections: false },
  { nameKey: 'profile', href: '/profile', icon: User, adminOnly: false, hasSubsections: false },
];

// QR Scanner navigation item (separate for easy removal)
const qrScannerNav = {
  nameKey: 'scan',
  href: '/scan',
  icon: Package, // This will be replaced with Scan icon in rendering
  adminOnly: false,
  isQRScanner: true,
  hasSubsections: false
};

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t, dir } = useLanguage();
  const { user, logout, isAdmin } = useUser();

  // Select navigation based on user role
  const currentNavigation = user?.role === 'customer' ? customerNavigation : navigation;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50" dir={dir}>
      {/* Mobile sidebar */}
      <div className={cn(
        "fixed inset-0 z-50 lg:hidden",
        sidebarOpen ? "block" : "hidden"
      )}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className={cn(
          "fixed inset-y-0 w-64 shadow-xl flex flex-col",
          dir === 'rtl' ? 'right-0' : 'left-0'
        )} style={{ backgroundColor: '#1e3a6e' }}>
          <div className="flex items-center justify-between p-4 border-b border-blue-800">
            <div className="flex items-center gap-2">
              <img src="/logo-icon.jpg" alt="" className="h-9 w-9 rounded-full object-cover border-2 border-white/30" />
              <span className="text-white font-bold text-base leading-tight">SourceMyGoods</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)} className="text-white hover:bg-blue-700">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="p-4 flex-1 overflow-y-auto">
            {currentNavigation.filter(item => !item.adminOnly || isAdmin).map((item) => {
              const Icon = item.icon;
              const [showSubsections, setShowSubsections] = useState(
                item.hasSubsections && location.pathname.startsWith(item.href)
              );

              return (
                <div key={item.nameKey}>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center px-3 py-2 text-sm font-medium rounded-md mb-1 transition-colors",
                      location.pathname === item.href
                        ? "text-white font-semibold"
                        : "text-blue-100 hover:text-white hover:bg-blue-700/60"
                    )}
                    style={location.pathname === item.href ? { backgroundColor: '#f5a623' } : {}}
                    onClick={(e) => {
                      if (item.hasSubsections) {
                        e.preventDefault();
                        setShowSubsections(!showSubsections);
                      } else {
                        setSidebarOpen(false);
                      }
                    }}
                  >
                    <Icon className={cn(dir === 'rtl' ? 'ml-3' : 'mr-3', 'h-5 w-5')} />
                    {t(item.nameKey)}
                    {item.hasSubsections && (
                      <ChevronDown
                        className={cn(
                          dir === 'rtl' ? 'mr-auto' : 'ml-auto',
                          'h-4 w-4 transition-transform duration-200',
                          showSubsections ? 'rotate-180' : ''
                        )}
                      />
                    )}
                  </Link>

                  {/* Subsections */}
                  {item.hasSubsections && showSubsections && (
                    <div className="ml-2 mt-1 space-y-1 mb-2">
                      {item.subsections?.map((subsection) => (
                        <Link
                          key={subsection.key}
                          to={`${item.href}/${subsection.key}`}
                          className={cn(
                            "flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            location.pathname === `${item.href}/${subsection.key}`
                              ? "text-white border-l-2"
                              : "text-blue-200 hover:text-white hover:bg-blue-700/50"
                          )}
                          style={location.pathname === `${item.href}/${subsection.key}` ? { borderColor: '#f5a623', backgroundColor: 'rgba(245,166,35,0.15)' } : {}}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <div className="w-1 h-1 rounded-full bg-blue-300 mr-2" />
                          {t(subsection.nameKey)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* QR Scanner Button - Mobile */}
            <div className="mt-4 pt-4 border-t border-blue-700/50">
              <QRButton 
                onClick={() => {
                  navigate('/scan');
                  setSidebarOpen(false);
                }} 
                className="w-full justify-start text-blue-100 hover:text-white hover:bg-blue-700/60" 
              />
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col lg:border-r" style={{ backgroundColor: '#1e3a6e' }}>
        <div className="flex items-center justify-center gap-3 p-4 border-b border-blue-800 cursor-pointer" onClick={() => navigate('/')}>
          <img src="/logo-icon.jpg" alt="SourceMyGoods" className="h-11 w-11 rounded-full object-cover border-2 border-white/30" />
          <span className="text-white font-bold text-base leading-tight">SourceMyGoods</span>
        </div>
        <nav className="flex-1 p-4">
          {currentNavigation.filter(item => !item.adminOnly || isAdmin).map((item) => {
            const Icon = item.icon;
            const [showSubsections, setShowSubsections] = useState(
              item.hasSubsections && location.pathname.startsWith(item.href)
            );

            return (
              <div key={item.nameKey}>
                <Link
                  to={item.href}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm font-medium rounded-md mb-1 cursor-pointer transition-colors",
                    location.pathname === item.href
                      ? "text-white font-semibold"
                      : "text-blue-100 hover:text-white hover:bg-blue-700/60"
                  )}
                  style={location.pathname === item.href ? { backgroundColor: '#f5a623' } : {}}
                  onClick={() => {
                    if (item.hasSubsections) {
                      setShowSubsections(!showSubsections);
                    }
                  }}
                >
                  <Icon className={cn(dir === 'rtl' ? 'ml-3' : 'mr-3', 'h-5 w-5')} />
                  {t(item.nameKey)}
                  {item.hasSubsections && (
                    <ChevronDown
                      className={cn(
                        dir === 'rtl' ? 'mr-2' : 'ml-2',
                        'h-4 w-4 transition-transform duration-200',
                        showSubsections ? 'rotate-180' : ''
                      )}
                    />
                  )}
                </Link>

                {/* Subsections */}
                {item.hasSubsections && showSubsections && (
                  <div className="ml-2 mt-1 space-y-1">
                    {item.subsections?.map((subsection) => (
                      <Link
                        key={subsection.key}
                        to={`${item.href}/${subsection.key}`}
                        className={cn(
                          "flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                          location.pathname === `${item.href}/${subsection.key}`
                            ? "text-white border-l-2"
                            : "text-blue-200 hover:text-white hover:bg-blue-700/50"
                        )}
                        style={location.pathname === `${item.href}/${subsection.key}` ? { borderColor: '#f5a623', backgroundColor: 'rgba(245,166,35,0.15)' } : {}}
                      >
                        <div className="w-1 h-1 rounded-full bg-blue-300 mr-2" />
                        {t(subsection.nameKey)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* QR Scanner Button - Easy to remove */}
          <div className="mt-4 pt-4 border-t border-blue-700/50">
            <QRButton onClick={() => navigate('/scan')} className="w-full justify-start text-blue-100 hover:text-white hover:bg-blue-700/60" />
          </div>
        </nav>
      </div>

      {/* Main content */}
      <div className={cn(dir === 'rtl' ? 'lg:pr-64' : 'lg:pl-64')}>
        {/* Top header - Fixed */}
        <header className="bg-white shadow-sm border-b sticky top-0 z-40">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <span className="text-sm text-gray-500 hidden md:block">
                {t('shippingManagementSystem')}
              </span>
            </div>

            <div className={cn("flex items-center gap-2", dir === 'rtl' ? 'flex-row-reverse' : '')}>
              {/* User Dropdown Menu */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center gap-2">
                      <UserCircle className="h-5 w-5 text-gray-600" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{user.username}</p>
                        <p className="text-xs text-gray-500">
                          {user.role === 'admin' ? t('admin') : user.warehouseName}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <User className="h-4 w-4 mr-2" />
                      {t('profile')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Language Selector - Compact */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
                className="flex items-center gap-1"
              >
                <Globe className="h-4 w-4" />
                <span className="text-xs font-medium">{language.toUpperCase()}</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}